import React from 'react';
import {
    Box,
    Paper,
    Typography,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from '@material-ui/core';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';

const toNumberOrNull = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};

const getQuestionOption = (question, response) => {
    if (!question) return null;
    const refId = Number(response?.scaleOptionId);
    return (question.questionOptionScores || []).find((opt) => {
        const optId = opt.scaleOptionId || opt.scaleOption?.id || opt.id;
        return Number(optId) === refId;
    });
};

const deriveScoreFromResponse = (response, question) => {
    const direct = toNumberOrNull(response?.score);
    if (direct != null) return direct;
    const numeric = toNumberOrNull(response?.numericValue);
    if (numeric != null) return numeric;
    const option = getQuestionOption(question, response);
    if (option) {
        const optScore = toNumberOrNull(option?.score);
        if (optScore != null) return optScore;
    }
    return null;
};

const interpretDepressionLevel = (score) => {
    if (score == null) return null;
    if (score <= 13) return 'Depresión minima';
    if (score <= 19) return 'Depresión leve';
    if (score <= 28) return 'Depresión moderada';
    return 'Depresión grave';
};

const computeIDBResults = (register, scale) => {
    if (!register || !scale) return null;
    const responses = Array.isArray(register?.scaleResponses) ? register.scaleResponses : [];
    const questions = Array.isArray(scale?.questions) ? scale.questions : [];
    if (!responses.length || !questions.length) return null;

    const questionById = new Map(questions.map((q) => [String(q.id), q]));
    let totalScore = 0;
    let totalMaxScore = 0;

    questions.forEach((question) => {
        const options = question.questionOptionScores || [];
        const maxScore = options.length
            ? Math.max(...options.map((opt) => toNumberOrNull(opt?.score) ?? 0), 0)
            : 0;
        totalMaxScore += maxScore;
    });

    responses.forEach((response) => {
        const question = questionById.get(String(response?.scaleQuestionId));
        if (!question) return;
        const score = deriveScoreFromResponse(response, question);
        if (score != null) totalScore += score;
    });

    const depressionLevel = interpretDepressionLevel(totalScore);

    return {
        totalScore,
        totalMaxScore: totalMaxScore || 63,
        depressionLevel,
    };
};

export const ScaleResultIDB = ({
    title = 'Resultados',
    totalScore,
    totalMaxScore = 63,
    depressionLevel,
    interpretationItems = [
        { label: '0-13: Depresión minima' },
        { label: '14-19: Depresión leve' },
        { label: '20-28: Depresión moderada' },
        { label: '29-63: Depresión grave' },
    ],
    onComputedResult,
    register,
    scale,
}) => {
    const derived = React.useMemo(() => computeIDBResults(register, scale), [register, scale]);
    React.useEffect(() => {
                // Usamos onComputedResult, que es lo que recibimos por props
                if (derived && typeof onComputedResult === 'function') {
                    onComputedResult(derived); 
                }
            }, [derived, onComputedResult]);
    const displayTotalScore = toNumberOrNull(totalScore) ?? derived?.totalScore ?? 0;
    const displayTotalMax = toNumberOrNull(totalMaxScore) ?? derived?.totalMaxScore ?? 63;
    const displayLevel = depressionLevel || derived?.depressionLevel;

    return (
        <Paper elevation={0} style={{ padding: 24, borderRadius: 12 }}>
            <Typography variant="h5" style={{ fontWeight: 700, marginBottom: 12, color: '#0d1b2a' }}>
                {title}
            </Typography>

            <List style={{ paddingTop: 0, marginBottom: 16 }}>
                <ListItem disableGutters>
                    <ListItemIcon style={{ minWidth: 24 }}>
                        <FiberManualRecordIcon fontSize="small" style={{ color: '#0d1b2a' }} />
                    </ListItemIcon>
                    <ListItemText
                        primaryTypographyProps={{ style: { color: '#0d1b2a', fontWeight: 600 } }}
                        primary={`Puntuación Total: ${displayTotalScore}/${displayTotalMax}`}
                    />
                </ListItem>
                {displayLevel && (
                    <ListItem disableGutters>
                        <ListItemIcon style={{ minWidth: 24 }}>
                            <FiberManualRecordIcon fontSize="small" style={{ color: '#0d1b2a' }} />
                        </ListItemIcon>
                        <ListItemText
                            primaryTypographyProps={{ style: { color: '#0d1b2a', fontWeight: 600 } }}
                            primary={`Nivel de Depresión: ${displayLevel}`}
                        />
                    </ListItem>
                )}
            </List>

            <Box mb={1} ml={1}>
                <Typography variant="body2" style={{ fontStyle: 'italic', color: '#0d1b2a' }}>
                    Interpretación:
                </Typography>
            </Box>

            <List style={{ paddingTop: 0 }}>
                {interpretationItems.map((item, index) => (
                    <ListItem key={item?.label ?? index} disableGutters>
                        <ListItemIcon style={{ minWidth: 24 }}>
                            <FiberManualRecordIcon fontSize="small" style={{ color: '#0d1b2a' }} />
                        </ListItemIcon>
                        <ListItemText
                            primaryTypographyProps={{ style: { color: '#0d1b2a' } }}
                            primary={item?.label || ''}
                        />
                    </ListItem>
                ))}
            </List>
        </Paper>
    );
};

export default ScaleResultIDB;
